import { useCallback, useMemo } from "react";

function toSet(indices) {
  return new Set(indices || []);
}

function isEmpty(value) {
  return value === null || value === undefined || value === "";
}

function clampNumber(value, min, max) {
  if (typeof value !== "number" || Number.isNaN(value)) return value;
  return Math.min(max, Math.max(min, value));
}

export function useMetadataIssues({ model }) {
  const {
    files,
    selectedIndices,
    selectedIndicesArray,
    applyBulkUpdatesToIndices,
    applyPerIndexUpdates,
  } = model;

  const allFileIssueMap = useMemo(() => {
    const map = new Map();

    files.forEach((file, index) => {
      const issues = [];
      const exif = file.modifiedExifData || {};

      if (isEmpty(exif.dateTaken)) {
        issues.push({ id: "missing-date", severity: "high", label: "Missing Date" });
      }

      const hasLat = !isEmpty(exif.latitude);
      const hasLon = !isEmpty(exif.longitude);

      if (hasLat !== hasLon) {
        issues.push({ id: "partial-gps", severity: "high", label: "Partial GPS" });
      }

      if (hasLat && (exif.latitude < -90 || exif.latitude > 90)) {
        issues.push({ id: "out-range-gps", severity: "high", label: "GPS Range" });
      }

      if (hasLon && (exif.longitude < -180 || exif.longitude > 180)) {
        issues.push({ id: "out-range-gps", severity: "high", label: "GPS Range" });
      }

      if (isEmpty(exif.copyright)) {
        issues.push({ id: "missing-copyright", severity: "medium", label: "No Copyright" });
      }

      map.set(index, issues);
    });

    return map;
  }, [files]);

  const selectionIssues = useMemo(() => {
    if (selectedIndices.size === 0) return [];

    const selectedFiles = selectedIndicesArray
      .map((index) => ({ index, file: files[index] }))
      .filter(({ file }) => Boolean(file));

    if (selectedFiles.length === 0) return [];

    const issues = [];

    const missingDateIndices = selectedFiles
      .filter(({ file }) => isEmpty(file.modifiedExifData?.dateTaken))
      .map(({ index }) => index);

    if (missingDateIndices.length > 0) {
      issues.push({
        id: "missing-date",
        type: "missing-field",
        severity: "high",
        scope: "selection",
        fieldIds: ["dateTaken"],
        affectedIndices: missingDateIndices,
        message: `${missingDateIndices.length} selected file${missingDateIndices.length > 1 ? "s" : ""} missing Date Taken`,
        fixes: [
          {
            id: "set-now",
            label: "Set Date Taken to now",
          },
        ],
      });
    }

    const partialGpsIndices = selectedFiles
      .filter(({ file }) => {
        const exif = file.modifiedExifData || {};
        const hasLat = !isEmpty(exif.latitude);
        const hasLon = !isEmpty(exif.longitude);
        return hasLat !== hasLon;
      })
      .map(({ index }) => index);

    if (partialGpsIndices.length > 0) {
      issues.push({
        id: "partial-gps",
        type: "incomplete-field",
        severity: "high",
        scope: "selection",
        fieldIds: ["latitude", "longitude"],
        affectedIndices: partialGpsIndices,
        message: `${partialGpsIndices.length} file${partialGpsIndices.length > 1 ? "s" : ""} contain partial GPS pairs`,
        fixes: [
          {
            id: "clear-partial",
            label: "Clear incomplete GPS values",
          },
        ],
      });
    }

    const outOfRangeGpsIndices = selectedFiles
      .filter(({ file }) => {
        const exif = file.modifiedExifData || {};
        const lat = exif.latitude;
        const lon = exif.longitude;
        return (
          (!isEmpty(lat) && (lat < -90 || lat > 90)) ||
          (!isEmpty(lon) && (lon < -180 || lon > 180))
        );
      })
      .map(({ index }) => index);

    if (outOfRangeGpsIndices.length > 0) {
      issues.push({
        id: "out-range-gps",
        type: "invalid-field",
        severity: "high",
        scope: "selection",
        fieldIds: ["latitude", "longitude"],
        affectedIndices: outOfRangeGpsIndices,
        message: `${outOfRangeGpsIndices.length} file${outOfRangeGpsIndices.length > 1 ? "s have" : " has"} invalid GPS ranges`,
        fixes: [
          {
            id: "clamp-range",
            label: "Clamp GPS to valid ranges",
          },
        ],
      });
    }

    const missingCopyrightIndices = selectedFiles
      .filter(({ file }) => isEmpty(file.modifiedExifData?.copyright))
      .map(({ index }) => index);

    if (missingCopyrightIndices.length > 0) {
      issues.push({
        id: "missing-copyright",
        type: "missing-field",
        severity: "medium",
        scope: "selection",
        fieldIds: ["copyright"],
        affectedIndices: missingCopyrightIndices,
        message: `${missingCopyrightIndices.length} file${missingCopyrightIndices.length > 1 ? "s" : ""} missing copyright`,
        fixes: [
          {
            id: "fill-copyright",
            label: "Set copyright placeholder",
          },
        ],
      });
    }

    if (selectedFiles.length > 1) {
      const makeValues = selectedFiles
        .map(({ file }) => file.modifiedExifData?.cameraMake)
        .filter((value) => !isEmpty(value));
      const modelValues = selectedFiles
        .map(({ file }) => file.modifiedExifData?.cameraModel)
        .filter((value) => !isEmpty(value));

      const uniqueMake = new Set(makeValues);
      const uniqueModel = new Set(modelValues);

      if (uniqueMake.size > 1 || uniqueModel.size > 1) {
        issues.push({
          id: "inconsistent-camera",
          type: "inconsistent-batch",
          severity: "medium",
          scope: "selection",
          fieldIds: ["cameraMake", "cameraModel"],
          affectedIndices: selectedIndicesArray,
          message: "Selected files have inconsistent camera metadata",
          fixes: [
            {
              id: "normalize-camera",
              label: "Use most common camera values",
            },
          ],
        });
      }
    }

    return issues;
  }, [files, selectedIndices, selectedIndicesArray]);

  const issueCounts = useMemo(() => {
    const counts = { high: 0, medium: 0, low: 0 };
    selectionIssues.forEach((issue) => {
      counts[issue.severity] += 1;
    });
    return counts;
  }, [selectionIssues]);

  const issuesByFile = useMemo(() => {
    const map = new Map();
    selectionIssues.forEach((issue) => {
      issue.affectedIndices.forEach((index) => {
        const current = map.get(index) || [];
        current.push(issue);
        map.set(index, current);
      });
    });
    return map;
  }, [selectionIssues]);

  const previewFix = useCallback(
    (issueId, fixId) => {
      const issue = selectionIssues.find((item) => item.id === issueId);
      if (!issue) return null;

      return {
        issueId,
        fixId,
        affectedCount: issue.affectedIndices.length,
        fields: issue.fieldIds,
      };
    },
    [selectionIssues],
  );

  const runFix = useCallback(
    (issueId, fixId) => {
      const issue = selectionIssues.find((item) => item.id === issueId);
      if (!issue) return;

      if (issueId === "missing-date" && fixId === "set-now") {
        const nowIso = new Date().toISOString();
        applyBulkUpdatesToIndices(
          issue.affectedIndices,
          { dateTaken: nowIso },
          `Set Date Taken for ${issue.affectedIndices.length} file${issue.affectedIndices.length > 1 ? "s" : ""}`,
        );
        return;
      }

      if (issueId === "partial-gps" && fixId === "clear-partial") {
        applyBulkUpdatesToIndices(
          issue.affectedIndices,
          { latitude: null, longitude: null, altitude: null },
          `Clear partial GPS data for ${issue.affectedIndices.length} file${issue.affectedIndices.length > 1 ? "s" : ""}`,
        );
        return;
      }

      if (issueId === "out-range-gps" && fixId === "clamp-range") {
        const updatesByIndex = {};
        issue.affectedIndices.forEach((index) => {
          const file = files[index];
          if (!file) return;

          const latitude = file.modifiedExifData?.latitude;
          const longitude = file.modifiedExifData?.longitude;

          updatesByIndex[index] = {
            latitude: clampNumber(latitude, -90, 90),
            longitude: clampNumber(longitude, -180, 180),
          };
        });

        applyPerIndexUpdates(
          updatesByIndex,
          `Clamp GPS range for ${issue.affectedIndices.length} file${issue.affectedIndices.length > 1 ? "s" : ""}`,
        );
        return;
      }

      if (issueId === "missing-copyright" && fixId === "fill-copyright") {
        const candidates = selectedIndicesArray
          .map((index) => files[index]?.modifiedExifData?.copyright)
          .filter((value) => !isEmpty(value));

        const value = candidates[0] || "Copyright";

        applyBulkUpdatesToIndices(
          issue.affectedIndices,
          { copyright: value },
          `Set copyright for ${issue.affectedIndices.length} file${issue.affectedIndices.length > 1 ? "s" : ""}`,
        );
        return;
      }

      if (issueId === "inconsistent-camera" && fixId === "normalize-camera") {
        const countValues = (fieldId) => {
          const counts = new Map();
          selectedIndicesArray.forEach((index) => {
            const value = files[index]?.modifiedExifData?.[fieldId];
            if (isEmpty(value)) return;
            const key = String(value);
            counts.set(key, { value, count: (counts.get(key)?.count || 0) + 1 });
          });

          return Array.from(counts.values()).sort((a, b) => b.count - a.count)[0]?.value;
        };

        const dominantMake = countValues("cameraMake") || "";
        const dominantModel = countValues("cameraModel") || "";

        applyBulkUpdatesToIndices(
          issue.affectedIndices,
          { cameraMake: dominantMake, cameraModel: dominantModel },
          `Normalize camera metadata across ${issue.affectedIndices.length} file${issue.affectedIndices.length > 1 ? "s" : ""}`,
        );
      }
    },
    [
      selectionIssues,
      selectedIndicesArray,
      files,
      applyBulkUpdatesToIndices,
      applyPerIndexUpdates,
    ],
  );

  return {
    issues: selectionIssues,
    issuesByFile,
    allFileIssueMap,
    issueCounts,
    runFix,
    previewFix,
  };
}

export default useMetadataIssues;
